import Image from 'next/image';
import { useState, useMemo, useEffect } from 'react';
import { useMediaDrop, MAX_FILE_SIZE } from '../../hooks/useMediaDrop';
import ImagesReviewModal from '../testimonials/imagesReviewModal';
import { v4 as uuidv4 } from 'uuid';
import { deleteFromS3, s3 } from '../../utils/s3';
import { PutObjectCommand } from '@aws-sdk/client-s3';

export interface ImageWithPreview {
  file: File;
  preview: string;
  uploadedUrl?: string;
  isUploading?: boolean;
  uploadError?: string;
  uploadProgress?: number;
}

interface MediaUploaderProps {
  images: ImageWithPreview[];
  setImages: React.Dispatch<React.SetStateAction<ImageWithPreview[]>>;
  isNotDesktop?: boolean;
  showTermsText?: boolean;
  onUploadStatusChange?: (isUploading: boolean) => void;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  images,
  setImages,
  isNotDesktop,
  onUploadStatusChange
}) => {
  const [showModal, setShowModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const uploadToS3 = async (file: File, index: number) => {
    try {
      setImages(prev => {
        const newImages = [...prev];
        newImages[index] = {
          ...newImages[index],
          isUploading: true,
          uploadProgress: 0,
          uploadError: undefined
        };
        return newImages;
      });

      const buffer = new Uint8Array(await file.arrayBuffer());
      const isVideo = file.type.startsWith('video');
      const type = isVideo ? 'video' : 'thumbnail';

      const normalizedFileName = isVideo
        ? file.name.replace(/.[^/.]+$/, '.mp4')
        : file.name;

      const fileName = `experiences/${
        type === 'video' ? '' : 'thumbnails/'
      }${uuidv4()}-${normalizedFileName}`;

      const uploadCommand = new PutObjectCommand({
        Bucket: process.env.NEXT_PUBLIC_AWS_BUCKET_NAME!,
        Key: fileName,
        Body: buffer,
        ContentType: isVideo ? 'video/mp4' : file.type
      });

      const updateProgress = () => {
        setImages(prev => {
          const newImages = [...prev];
          if (newImages[index] && newImages[index]?.isUploading) {
            const currentProgress = newImages[index]?.uploadProgress || 0;
            if (currentProgress < 90) {
              newImages[index] = {
                ...newImages[index],
                uploadProgress: Math.min(
                  90,
                  currentProgress + Math.random() * 20
                )
              };
            }
          }
          return newImages;
        });
      };

      const progressInterval = setInterval(updateProgress, 500);

      try {
        await s3.send(uploadCommand);
        clearInterval(progressInterval);
      } catch (error) {
        clearInterval(progressInterval);
        throw error;
      }
      const bucket = process.env.NEXT_PUBLIC_AWS_BUCKET_NAME!;
      const region = process.env.NEXT_PUBLIC_AWS_REGION!;
      const uploadedUrl = `https://${bucket}.s3.${region}.amazonaws.com/${fileName}`;

      setImages(prev => {
        const newImages = [...prev];
        if (newImages[index]) {
          newImages[index] = {
            ...newImages[index],
            isUploading: false,
            uploadedUrl,
            uploadProgress: 100
          };
        }
        return newImages;
      });

      return uploadedUrl;
    } catch (error) {
      setImages(prev => {
        const newImages = [...prev];
        if (newImages[index]) {
          newImages[index] = {
            ...newImages[index],
            isUploading: false,
            uploadError: 'Failed to upload. Click to retry.'
          };
        }
        return newImages;
      });
      console.error('Upload error:', error);
      return null;
    }
  };

  const handleRemoveImage = async (index: number) => {
    const image = images[index];

    if (image?.uploadedUrl) {
      await deleteFromS3(image?.uploadedUrl);
    }

    setImages(prev => {
      const newImages = [...prev];
      if (newImages[index]?.preview) {
        URL.revokeObjectURL(newImages[index]?.preview);
      }
      newImages?.splice(index, 1);
      return newImages;
    });
  };

  const handleRetryUpload = async (index: number) => {
    const image = images[index];
    if (image?.file) {
      await uploadToS3(image.file, index);
    }
  };

  const isAnyUploading = useMemo(() => {
    return images.some(img => img?.isUploading);
  }, [images]);

  useEffect(() => {
    onUploadStatusChange?.(isAnyUploading);
  }, [isAnyUploading, onUploadStatusChange]);

  const { getRootProps, getInputProps, isDragActive } = useMediaDrop({
    onImagesChange: async (newImages: ImageWithPreview[]) => {
      setImages(prev => [...prev, ...newImages]);
      const startIndex = images.length;
      newImages.forEach(async (img, idx) => {
        await uploadToS3(img.file, startIndex + idx);
      });
    },
    maxFileSize: MAX_FILE_SIZE
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className="cursor-pointer border-2 border-dashed bg-gray-100 rounded-lg py-6 px-4 text-center mt-2"
      >
        <input
          {...getInputProps({
            accept: 'image/*,video/*',
            capture: undefined
          })}
        />
        <div className="flex justify-center">
          <Image
            src="/blue-camera.svg"
            alt="Upload"
            width={32}
            height={32}
            draggable="false"
            className="object-contain w-[24px] h-[24px] md:w-[32px] md:h-[32px]"
          />
        </div>

        <p className="text-[12px] md:text-[14px] xl:text-[16px] text-[#555555]">
          {isNotDesktop
            ? 'Click to upload your media'
            : isDragActive
            ? 'Drop your media here'
            : 'Drop your media here or click to upload'}
        </p>
        <p className="text-[9px] md:text-[12px] xl:text-[14px] text-[#999999]">
          Images and Videos only (upto 500MB each)
        </p>
      </div>

      {images?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {images?.map((img, idx) => {
            const isVideo = img?.file?.type?.startsWith('video');

            return (
              <div
                key={idx}
                className="relative w-16 h-16 cursor-pointer"
                onClick={() => {
                  if (img?.uploadError) {
                    handleRetryUpload(idx);
                  } else {
                    setShowModal(true);
                    setActiveIndex(idx);
                  }
                }}
              >
                {isVideo ? (
                  <div className="relative w-full h-full">
                    <video
                      src={img?.preview}
                      controls={false}
                      className="object-cover rounded-lg w-full h-full pointer-events-none"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-black bg-opacity-50 rounded-lg p-5">
                        <Image
                          src="/play-button-white.svg"
                          alt="play icon"
                          width={24}
                          height={24}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <Image
                    src={img?.preview}
                    alt={`Preview ${idx}`}
                    fill
                    unoptimized
                    className="object-cover rounded"
                  />
                )}

                {img?.isUploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded">
                    <div className="relative w-12 h-12">
                      <svg className="transform -rotate-90 w-12 h-12">
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          stroke="#4B5563"
                          strokeWidth="4"
                          fill="none"
                          className="opacity-25"
                        />
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          stroke="#3B82F6"
                          strokeWidth="4"
                          fill="none"
                          strokeDasharray={125.6}
                          strokeDashoffset={
                            125.6 * (1 - (img?.uploadProgress || 0) / 100)
                          }
                          className="transition-all duration-300"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-white text-xs font-medium">
                          {Math.min(100, Math.round(img?.uploadProgress || 0))}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {img?.uploadError && (
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      handleRetryUpload(idx);
                    }}
                    className="absolute inset-0 bg-red-500 bg-opacity-50 flex flex-col items-center justify-center rounded cursor-pointer hover:bg-opacity-60"
                  >
                    <Image
                      src="/retry-icon.svg"
                      alt="retry"
                      width={20}
                      height={20}
                    />
                    <span className="text-white text-xs mt-1">Retry</span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    handleRemoveImage(idx);
                  }}
                  disabled={img?.isUploading}
                  className={`absolute -top-1 -right-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ${
                    img?.isUploading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 cursor-pointer'
                  }`}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <ImagesReviewModal
          data={{
            isVideo: images?.map(img => img?.file?.type?.startsWith('video')),
            fileTypes: images?.map(img => img?.file?.type)
          }}
          setShowModal={setShowModal}
          productName="Preview Media"
          randomName="Preview"
          media={images?.map(img => img?.uploadedUrl || img?.preview)}
          initialIndex={activeIndex}
        />
      )}
    </div>
  );
};

export default MediaUploader;
